// Map PatternState + output dimensions → DotRenderConfig for image halftone.

import type { Palette, PatternParams, PatternState } from '../../types';
import { hashSeed } from '../dot-engine/math';
import {
  buildDots,
  type DotRenderConfig,
  drawCanvas,
  type GridType,
} from '../dot-engine/render-dots';
import type { DensityFieldConfig } from '../dot-engine/types';
import { buildImageHalftoneSampling, imageColorAt, imageDensityAt } from './density';

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Shorter canvas edge at which `spacing` is interpreted literally (cover banner). */
export const HALFTONE_SPACING_REFERENCE = 400;

function paletteToDotColors(palette: Palette): readonly [string, string, string, string] {
  const c0 = palette[0] ?? '#000000';
  const c1 = palette[1] ?? c0;
  const c2 = palette[2] ?? c1;
  const c3 = palette[3] ?? c2;
  return [c0, c1, c2, c3];
}

function scaledSpacing(spacing: number, width: number, height: number): number {
  const shorter = Math.min(width, height);
  return spacing * (shorter / HALFTONE_SPACING_REFERENCE);
}

function stubDensityConfig(width: number, height: number, seed: number): DensityFieldConfig {
  return {
    width,
    height,
    mode: 'image',
    seed: hashSeed(String(seed)),
    derived: null,
    srcLayout: 'scatter',
    srcCount: 1,
    srcSize: 0.5,
    srcElong: 1,
    srcFalloff: 0.5,
    srcDivide: 0,
    convN: 1,
    convGap: 0,
    convRot: 0,
    convTwist: 0,
    kaleN: 1,
    kaleRot: 0,
    matrixCell: 40,
    matrixFill: 0.7,
    matrixLevels: 1,
    matrixBias: 'scatter',
    _cache: {},
  };
}

export function buildImageHalftoneConfig(
  state: PatternState,
  width: number,
  height: number,
  params: PatternParams,
): DotRenderConfig {
  const densityStub = stubDensityConfig(width, height, state.seed);
  const gridType: GridType = params.gridType === 'radial' ? 'radial' : 'square';

  return {
    ...densityStub,
    spacing: scaledSpacing(params.spacing, width, height),
    minFrac: Math.min(1, params.minDot / 100),
    maxFrac: Math.min(0.85, params.maxDot / 100),
    intensity: params.intensity,
    shape: 'slasho',
    seamless: false,
    shapePow: params.falloff,
    coverage: params.coverage,
    baseFill: params.baseFill,
    gridType,
    gridAngle: params.gridAngle,
    mark: 'off',
    markShape: 'ring',
    markLayout: 'trio',
    markPolarity: 'positive',
    markSize: 0.5,
    markThick: 0.55,
    markStrength: 0.3,
    markMask: null,
    markVariant: 'axis',
    colors: paletteToDotColors(state.palette),
    background: state.bg,
    backgroundMode: 'transparent',
    fadeMode: 'none',
    fadeSize: 0.16,
    fadeRoughness: 0.42,
    shine: false,
    shineAngle: 135,
    strokeWeight: params.strokeWeight,
    jitterDots: true,
    colorAt: null,
  };
}

export function renderImageHalftone(
  ctx: Ctx2D,
  width: number,
  height: number,
  state: PatternState,
): void {
  const sampled = state.image.sampled;
  if (!state.image.loaded || sampled === null) {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.font = `${Math.min(width, height) * 0.03}px Inter, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Upload an image to start', width / 2, height / 2);
    return;
  }

  const params = state.params;
  const sampling = buildImageHalftoneSampling(sampled, width, height, params);
  const baseCfg = buildImageHalftoneConfig(state, width, height, params);
  const cfg: DotRenderConfig = {
    ...baseCfg,
    colorAt: (x: number, y: number): string =>
      imageColorAt(sampled, x, y, sampling) ?? baseCfg.colors[0],
  };

  const dots = buildDots(cfg, (x, y) => imageDensityAt(sampled, x, y, sampling));
  drawCanvas(ctx, dots, cfg);
}
